import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Card, Row, Col, ListGroup, Badge } from "react-bootstrap";
import { FaTrash, FaSearch, FaCamera, FaUsers, FaExclamationTriangle, FaInfoCircle } from "react-icons/fa";
import { getClassesBySchoolId, softDeleteClass, whoami } from "../services/APIClient";

function DeleteClassesModal({ show, onHide, schoolid }) {
  const [allClasses, setAllClasses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState(null);
  const [deletionReason, setDeletionReason] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [alertVariant, setAlertVariant] = useState("");
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [teacherId, setTeacherId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Filter states
  const [classGradeFilter, setClassGradeFilter] = useState("all");
  const [classAcademicYearFilter, setClassAcademicYearFilter] = useState("all");
  const [classSnapshotFilter, setClassSnapshotFilter] = useState("all");

  // Available grade levels using boolean system
  const gradeLevels = [
    { value: "all", label: "All Grades" },
    { value: "grade_level1", label: "Junior Infants" },
    { value: "grade_level2", label: "Senior Infants" },
    { value: "grade_level3", label: "1st Class" },
    { value: "grade_level4", label: "2nd Class" },
    { value: "grade_level5", label: "3rd Class" },
    { value: "grade_level6", label: "4th Class" },
    { value: "grade_level7", label: "5th Class" },
    { value: "grade_level8", label: "6th Class" }
  ];

  // Academic years - will be populated from actual data
  const [availableAcademicYears, setAvailableAcademicYears] = useState([
    { value: "all", label: "All Academic Years" }
  ]);

  // Snapshot filter options
  const snapshotOptions = [
    { value: "all", label: "All Classes" },
    { value: "active", label: "Active Classes Only" },
    { value: "snapshot", label: "Snapshots Only" }
  ];

  const getGradeLabelFromClass = (classItem) => {
    if (classItem.grade_level1) return "Junior Infants";
    if (classItem.grade_level2) return "Senior Infants";
    if (classItem.grade_level3) return "1st Class";
    if (classItem.grade_level4) return "2nd Class";
    if (classItem.grade_level5) return "3rd Class";
    if (classItem.grade_level6) return "4th Class";
    if (classItem.grade_level7) return "5th Class";
    if (classItem.grade_level8) return "6th Class";
    return "Unknown Grade";
  };

  const getGradeBooleanFromClass = (classItem) => {
    if (classItem.grade_level1) return "grade_level1";
    if (classItem.grade_level2) return "grade_level2";
    if (classItem.grade_level3) return "grade_level3";
    if (classItem.grade_level4) return "grade_level4";
    if (classItem.grade_level5) return "grade_level5";
    if (classItem.grade_level6) return "grade_level6";
    if (classItem.grade_level7) return "grade_level7";
    if (classItem.grade_level8) return "grade_level8";
    return "unknown";
  };

  // Fetch teacher ID and classes when the modal opens
  useEffect(() => {
    if (show) {
      setLoading(true);
      
      // Get current teacher ID
      whoami()
        .then((response) => {
          setTeacherId(response.data.stakeholder.id);
        })
        .catch((error) => {
          console.error("Error fetching teacher info:", error);
          setStatusMessage("Failed to load teacher information. Please refresh and try again.");
          setAlertVariant("danger");
        });

      // Get classes for the school
      if (schoolid) {
        getClassesBySchoolId(schoolid)
          .then((response) => {
            // Transform classes data to include enhanced properties
            const transformedClasses = (response || []).map(classItem => {
              const gradeLabel = getGradeLabelFromClass(classItem);
              const gradeBoolean = getGradeBooleanFromClass(classItem);
              
              return {
                id: classItem.id,
                name: classItem.name || classItem.className || classItem.class_name || 'Unnamed Class',
                grade: gradeLabel,
                gradeBoolean: gradeBoolean,
                academicYear: classItem.academicYear || null,
                snapshot: classItem.snapshot || false,
                studentNumber: classItem.studentNumber || 0,
                active: classItem.active,
                trial_class: classItem.trial_class,
                date_updated: classItem.date_updated,
                // Keep original fields for compatibility
                className: classItem.className,
                class_name: classItem.class_name,
                year: classItem.year
              };
            }).sort((a, b) => {
              // Sort by academic year (newest first), then by name
              if (a.academicYear !== b.academicYear) {
                return b.academicYear?.localeCompare(a.academicYear || '') || 0;
              }
              return a.name.localeCompare(b.name);
            });

            // Extract unique academic years for filter options
            const uniqueAcademicYears = [...new Set(transformedClasses
              .map(cls => cls.academicYear)
              .filter(year => year) // Remove null/undefined
            )].sort().reverse(); // Sort newest first

            const academicYearOptions = [
              { value: "all", label: "All Academic Years" },
              ...uniqueAcademicYears.map(year => ({
                value: year,
                label: `${year} Academic Year`
              }))
            ];

            setAvailableAcademicYears(academicYearOptions);
            setAllClasses(transformedClasses);
          })
          .catch((error) => {
            console.error("Error fetching classes:", error);
            setStatusMessage("Failed to load classes. Please try again.");
            setAlertVariant("danger");
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    }
  }, [schoolid, show]);

  // Get filtered classes based on all filter criteria
  const getFilteredClasses = () => {
    return allClasses.filter(classItem => {
      const matchesGrade = classGradeFilter === "all" || classItem.gradeBoolean === classGradeFilter;
      const matchesAcademicYear = classAcademicYearFilter === "all" || classItem.academicYear === classAcademicYearFilter;
      
      // Snapshot filtering logic
      let matchesSnapshot = true;
      if (classSnapshotFilter === "active") {
        matchesSnapshot = !classItem.snapshot;
      } else if (classSnapshotFilter === "snapshot") {
        matchesSnapshot = classItem.snapshot;
      }
      
      const matchesSearch = classItem.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           classItem.grade?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesGrade && matchesAcademicYear && matchesSnapshot && matchesSearch;
    });
  };

  // Handle class deletion confirmation
  const handleDeleteClass = () => {
    if (!selectedClass) {
      setStatusMessage("Please select a class to delete.");
      setAlertVariant("warning");
      return;
    }
    if (!deletionReason.trim()) {
      setStatusMessage("Please provide a reason for deletion.");
      setAlertVariant("warning");
      return;
    }
    setConfirmationVisible(true);
  };

  // Handle the actual delete API call after confirmation
  const confirmDeleteClass = () => {
    if (selectedClass && teacherId) {
      setIsDeleting(true);
      const selectedClassData = allClasses.find(c => c.id === selectedClass);
      
      // Prepare data for the soft delete API
      const deleteData = {
        classid: selectedClass,
        deletionReason: deletionReason.trim(),
        teacherId: teacherId
      };

      softDeleteClass(schoolid, deleteData)
        .then(() => {
          setStatusMessage(
            `Class "${selectedClassData?.name}" was successfully soft deleted! Reason: ${deletionReason.trim()}`
          );
          setAlertVariant("success");
          
          // Remove the deleted class from the local state
          setAllClasses(prevClasses => prevClasses.filter(c => c.id !== selectedClass));
          
          clearFields();
          
          // Log the deletion for audit purposes
          console.log("Class soft deleted:", {
            classId: selectedClass,
            className: selectedClassData?.name,
            reason: deleteData.deletionReason,
            deletedBy: teacherId,
            timestamp: new Date().toISOString()
          });
        })
        .catch((error) => {
          console.error("Soft delete error:", error);
          setStatusMessage("Failed to delete the class. Please try again or contact support.");
          setAlertVariant("danger");
        })
        .finally(() => {
          setIsDeleting(false);
          setConfirmationVisible(false);
        });
    } else {
      setStatusMessage("Missing required information. Please refresh and try again.");
      setAlertVariant("danger");
      setIsDeleting(false);
      setConfirmationVisible(false);
    }
  };

  // Clear the modal fields and messages
  const clearFields = () => {
    setSelectedClass(null);
    setDeletionReason("");
    setStatusMessage("");
    setAlertVariant("");
    setConfirmationVisible(false);
  };

  const handleModalClose = () => {
    clearFields();
    setSearchTerm("");
    setClassGradeFilter("all");
    setClassAcademicYearFilter("all");
    setClassSnapshotFilter("all");
    onHide();
  };

  const filteredClasses = getFilteredClasses();
  const selectedClassData = allClasses.find(c => c.id === selectedClass);

  return (
    <Modal show={show} onHide={handleModalClose} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaTrash className="me-2 text-danger" />
          Soft Delete Classes
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ minHeight: '600px' }}>
        {/* Information Card */}
        <Alert variant="info" className="mb-4">
          <FaInfoCircle className="me-2" />
          <strong>Soft Delete:</strong> This will soft delete the class, marking it as inactive while preserving all data. 
          The class and its associated data (students, assignments, grades) will be hidden but can be recovered if needed.
        </Alert>

        {statusMessage && (
          <Alert variant={alertVariant} className="mb-3" dismissible onClose={() => setStatusMessage("")}>
            {statusMessage}
          </Alert>
        )}

        {!confirmationVisible && (
          <>
            {/* Class Filters */}
            <div className="mb-4">
              <h6 className="mb-3">Filter Classes</h6>
              <Row>
                <Col md={3}>
                  <Form.Select 
                    value={classAcademicYearFilter} 
                    onChange={(e) => setClassAcademicYearFilter(e.target.value)}
                    size="sm"
                    disabled={isDeleting}
                  >
                    {availableAcademicYears.map(year => (
                      <option key={year.value} value={year.value}>
                        {year.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Select 
                    value={classGradeFilter} 
                    onChange={(e) => setClassGradeFilter(e.target.value)}
                    size="sm"
                    disabled={isDeleting}
                  >
                    {gradeLevels.map(grade => (
                      <option key={grade.value} value={grade.value}>
                        {grade.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Select 
                    value={classSnapshotFilter} 
                    onChange={(e) => setClassSnapshotFilter(e.target.value)}
                    size="sm"
                    disabled={isDeleting}
                  >
                    {snapshotOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <div className="position-relative">
                    <Form.Control
                      type="text"
                      placeholder="Search classes..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      size="sm"
                      disabled={isDeleting}
                    />
                    <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                  </div>
                </Col>
              </Row>
              <div className="mt-2">
                <small className="text-muted">
                  {filteredClasses.length} of {allClasses.length} classes shown
                  {(searchTerm || classGradeFilter !== "all" || classAcademicYearFilter !== "all" || classSnapshotFilter !== "all") && 
                    " (filtered)"}
                </small>
              </div>
            </div>

            {/* Class List Display */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">
                  Select Class to Delete
                  {selectedClass && (
                    <Badge bg="danger" className="ms-2">Selected</Badge>
                  )}
                </h6>
              </div>
              
              <div style={{ height: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.375rem' }}>
                {loading ? (
                  <div className="text-center py-4">Loading classes...</div>
                ) : filteredClasses.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    {allClasses.length === 0 ? "No classes available for this school" : "No classes match the current filters"}
                  </div>
                ) : (
                  <ListGroup variant="flush">
                    {filteredClasses.map((cls) => (
                      <ListGroup.Item
                        key={cls.id}
                        className={`cursor-pointer ${selectedClass === cls.id ? 'bg-danger-light border-danger' : ''}`}
                        onClick={() => setSelectedClass(cls.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-2">
                              <div className="fw-medium me-2">{cls.name}</div>
                              {cls.snapshot && (
                                <Badge bg="secondary" className="me-2">
                                  <FaCamera className="me-1" size={10} />
                                  Snapshot
                                </Badge>
                              )}
                              {cls.trial_class && (
                                <Badge bg="warning" className="me-2">Trial</Badge>
                              )}
                            </div>
                            
                            <div className="small text-muted">
                              <div>
                                <strong>Grade:</strong> {cls.grade}
                                {cls.academicYear && ` • ${cls.academicYear}`}
                                {!cls.snapshot && cls.studentNumber > 0 && (
                                  <Badge bg="light" text="dark" className="ms-2">
                                    <FaUsers className="me-1" size={10} />
                                    {cls.studentNumber} student{cls.studentNumber !== 1 ? 's' : ''}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          {selectedClass === cls.id && (
                            <div className="text-danger">
                              <FaTrash />
                            </div>
                          )}
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </div>
            </div>

            {/* Selected Class Information */}
            {selectedClass && selectedClassData && (
              <Alert variant="warning" className="mb-4">
                <div className="d-flex align-items-center mb-2">
                  <FaExclamationTriangle className="me-2" />
                  <strong>Selected for Deletion:</strong>
                </div>
                <div className="d-flex align-items-center">
                  <span className="me-2">{selectedClassData.name}</span>
                  {selectedClassData.snapshot && (
                    <Badge bg="secondary" className="me-2">
                      <FaCamera className="me-1" size={10} />
                      Snapshot
                    </Badge>
                  )}
                  <span className="small text-muted">
                    ({selectedClassData.grade}
                    {selectedClassData.academicYear && ` • ${selectedClassData.academicYear}`}
                    {!selectedClassData.snapshot && selectedClassData.studentNumber > 0 && 
                      ` • ${selectedClassData.studentNumber} students`}
                    )
                  </span>
                </div>
              </Alert>
            )}

            {/* Deletion Reason */}
            {selectedClass && (
              <Form.Group className="mb-4">
                <Form.Label className="fw-medium">
                  Reason for Deletion <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Please provide a reason for soft deleting this class (e.g., 'Class cancelled due to low enrollment', 'Merged with another class', 'End of term', etc.)"
                  value={deletionReason}
                  onChange={(e) => setDeletionReason(e.target.value)}
                  disabled={isDeleting}
                  className={!deletionReason.trim() && selectedClass ? 'border-warning' : ''}
                />
                <Form.Text className="text-muted">
                  This information will be logged for audit purposes and cannot be changed after deletion.
                </Form.Text>
              </Form.Group>
            )}

            {/* Snapshot Warning */}
            {selectedClassData?.snapshot && (
              <Alert variant="warning" className="mb-4">
                <FaCamera className="me-2" />
                <strong>Snapshot Class Warning:</strong> You are about to delete a snapshot class. 
                Snapshot classes contain historical data that may be important for record keeping.
              </Alert>
            )}
          </>
        )}

        {/* Confirmation section */}
        {confirmationVisible && selectedClassData && (
          <Card className="bg-danger-light border-danger">
            <Card.Body>
              <div className="text-center mb-4">
                <div className="bg-danger rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                     style={{ width: '60px', height: '60px' }}>
                  <FaTrash className="text-white" size={24} />
                </div>
                <h5 className="mb-2 text-danger">Confirm Class Deletion</h5>
                <p className="text-muted">This action will soft delete the class. Please review carefully.</p>
              </div>

              <div className="mb-4 p-3 bg-white rounded border">
                <div className="d-flex align-items-center mb-2">
                  <strong>Class:</strong>
                  <span className="ms-2 me-2">{selectedClassData.name}</span>
                  {selectedClassData.snapshot && (
                    <Badge bg="secondary">
                      <FaCamera className="me-1" size={10} />
                      Snapshot
                    </Badge>
                  )}
                </div>
                <div className="mb-2">
                  <strong>Details:</strong> {selectedClassData.grade}
                  {selectedClassData.academicYear && ` • ${selectedClassData.academicYear}`}
                  {!selectedClassData.snapshot && selectedClassData.studentNumber > 0 && 
                    ` • ${selectedClassData.studentNumber} students enrolled`}
                </div>
                <div>
                  <strong>Deletion Reason:</strong>
                  <div className="mt-1 p-2 bg-light rounded border">
                    {deletionReason.trim()}
                  </div>
                </div>
              </div>

              <Alert variant="danger" className="mb-4">
                <FaExclamationTriangle className="me-2" />
                <strong>Important:</strong> This will soft delete the class, hiding it from normal operations 
                while preserving all data. The class and its students, assignments, and grades will be archived 
                but can be recovered if needed.
              </Alert>
              
              <div className="d-flex justify-content-center gap-3">
                <Button 
                  variant="danger" 
                  onClick={confirmDeleteClass}
                  disabled={isDeleting}
                  size="lg"
                >
                  {isDeleting ? "Deleting..." : "Confirm Soft Delete"}
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={() => setConfirmationVisible(false)}
                  disabled={isDeleting}
                  size="lg"
                >
                  Cancel
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div className="text-muted">
            {selectedClass && selectedClassData && !confirmationVisible && (
              <span>
                <FaTrash className="me-1 text-danger" />
                Ready to delete: {selectedClassData.name}
                {selectedClassData.snapshot && (
                  <Badge bg="warning" className="ms-2 text-dark">
                    <FaCamera className="me-1" size={10} />
                    Snapshot Class
                  </Badge>
                )}
              </span>
            )}
          </div>
          <div>
            {!confirmationVisible && (
              <Button 
                variant="secondary" 
                onClick={handleModalClose} 
                disabled={isDeleting}
                className="me-2"
              >
                Cancel
              </Button>
            )}
            {!confirmationVisible && selectedClass && (
              <Button 
                variant="danger" 
                onClick={handleDeleteClass}
                disabled={isDeleting || !deletionReason.trim()}
              >
                <FaTrash className="me-1" />
                {isDeleting ? "Processing..." : "Soft Delete Class"}
              </Button>
            )}
            {confirmationVisible && (
              <Button variant="secondary" onClick={handleModalClose} disabled={isDeleting}>
                Close
              </Button>
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default DeleteClassesModal;